import React, { useState, useRef, useEffect } from "react";
import { Card, IconButton } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import { Panel, PanelGroup } from "react-resizable-panels";
import Editor from "@monaco-editor/react";
import StyledPanelResizeHandle from "components/StyledPanelResizeHandle/StyledPanelResizeHandle";
import Div from "@jumbo/shared/Div";
import { TabPanel } from "components/TabPanel";
import { StyledTab, StyledTabs } from "components/CustomTabs";

const CodeEditorAndOutputPanel = ({
  code,
  setCode,
  editorLanguage,
  headerControls,
  codeTabs,
  outputTabs,
  outputContent,
  onEditorMount,
  theme,
  defaultCodeSize = 50,
  defaultOutputSize = 50,
  codeTabValue,
  setCodeTabValue,
  ioTabValue,
  handleIOTabChange,
}) => {
  const [isCodePanelOpen, setIsCodePanelOpen] = useState(true);
  const [isOutputPanelOpen, setIsOutputPanelOpen] = useState(true);

  const codePanelRef = useRef(null);
  const outputPanelRef = useRef(null);

  // Toggle panel visibility
  const toggleCodePanel = () => {
    if (!isOutputPanelOpen && isCodePanelOpen) return;
    if (codePanelRef.current) {
      isCodePanelOpen
        ? codePanelRef.current.collapse()
        : codePanelRef.current.expand();
    }
  };

  const toggleOutputPanel = () => {
    if (!isCodePanelOpen && isOutputPanelOpen) return;
    if (outputPanelRef.current) {
      isOutputPanelOpen
        ? outputPanelRef.current.collapse()
        : outputPanelRef.current.expand();
    }
  };

  // Adjust panel sizes based on collapse state
  useEffect(() => {
    if (codePanelRef.current && outputPanelRef.current) {
      if (!isCodePanelOpen) {
        codePanelRef.current.resize(15);
        outputPanelRef.current.resize(85);
      } else if (!isOutputPanelOpen) {
        outputPanelRef.current.resize(15);
        codePanelRef.current.resize(85);
      } else {
        codePanelRef.current.resize(defaultCodeSize);
        outputPanelRef.current.resize(defaultOutputSize);
      }
    }
  }, [isCodePanelOpen, isOutputPanelOpen, defaultCodeSize, defaultOutputSize]);

  return (
    <Panel defaultSize={50} minSize={20}>
      <PanelGroup direction="vertical" style={{ height: "100%" }}>
        {/* Code Editor Section */}
        <Panel
          ref={codePanelRef}
          defaultSize={defaultCodeSize}
          minSize={20}
          collapsedSize={9}
          collapsible
          onCollapse={(collapsed) => setIsCodePanelOpen(!collapsed)}
        >
          {isCodePanelOpen ? (
            <Card
              sx={{
                height: "100%",
                display: "flex",
                flexDirection: "column",
                borderRadius: 0,
              }}
            >
              <Div
                sx={{
                  borderBottom: 1,
                  borderColor: "divider",
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <StyledTabs
                  value={codeTabValue}
                  onChange={(_, newValue) => setCodeTabValue(newValue)}
                  sx={{ flexGrow: 1 }}
                  variant="scrollable"
                  scrollButtons="auto"
                  aria-label="Code panel tabs"
                >
                  {codeTabs.map((tab, index) => (
                    <StyledTab
                      key={index}
                      icon={tab.icon}
                      iconPosition="start"
                      label={tab.label}
                      aria-label={tab.label}
                    />
                  ))}
                </StyledTabs>
                {headerControls && <Div sx={{ mr: 1 }}>{headerControls}</Div>}
                <IconButton
                  onClick={toggleCodePanel}
                  aria-label="Collapse code panel"
                >
                  <ExpandMoreIcon />
                </IconButton>
              </Div>
              <Div sx={{ flexGrow: 1, overflow: "auto" }}>
                {codeTabs.map((tab, index) => (
                  <TabPanel
                    key={index}
                    value={codeTabValue}
                    index={index}
                    sx={{ p: 0 }}
                  >
                    {index === 0 ? (
                      <Editor
                        height="100%"
                        width="100%"
                        theme={theme.mode === "dark" ? "vs-dark" : "light"}
                        language={editorLanguage}
                        value={code}
                        onChange={setCode}
                        onMount={onEditorMount}
                        options={{
                          minimap: { enabled: false },
                          scrollBeyondLastLine: false,
                          automaticLayout: true,
                          wordWrap: "on",
                          folding: true,
                          renderWhitespace: "none",
                        }}
                      />
                    ) : (
                      tab.content
                    )}
                  </TabPanel>
                ))}
              </Div>
            </Card>
          ) : (
            <Card sx={{ display: "flex" }}>
              <StyledTabs
                value={codeTabValue}
                onChange={(_, newValue) => setCodeTabValue(newValue)}
                sx={{ flexGrow: 1 }}
                variant="scrollable"
                scrollButtons="auto"
                aria-label="Code panel tabs (collapsed)"
              >
                {codeTabs.map((tab, index) => (
                  <StyledTab
                    key={index}
                    icon={tab.icon}
                    iconPosition="start"
                    label={tab.label}
                    aria-label={tab.label}
                  />
                ))}
              </StyledTabs>
              <IconButton
                onClick={toggleCodePanel}
                aria-label="Expand code panel"
              >
                <ExpandLessIcon />
              </IconButton>
            </Card>
          )}
        </Panel>

        {/* Resize Handle */}
        <StyledPanelResizeHandle direction="horizontal" />

        {/* Output Section */}
        <Panel
          ref={outputPanelRef}
          defaultSize={defaultOutputSize}
          minSize={20}
          collapsedSize={9}
          collapsible
          onCollapse={(collapsed) => setIsOutputPanelOpen(!collapsed)}
        >
          {isOutputPanelOpen ? (
            <Card
              sx={{
                height: "100%",
                display: "flex",
                flexDirection: "column",
                borderRadius: 0,
              }}
            >
              <Div
                sx={{
                  borderBottom: 1,
                  borderColor: "divider",
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <StyledTabs
                  value={ioTabValue}
                  onChange={(_, newValue) => handleIOTabChange(_, newValue)}
                  sx={{ flexGrow: 1 }}
                  variant="scrollable"
                  scrollButtons="auto"
                  aria-label="Output panel tabs"
                >
                  {outputTabs.map((tab, index) => (
                    <StyledTab
                      key={index}
                      icon={tab.icon}
                      iconPosition="start"
                      label={tab.label}
                      aria-label={tab.label}
                    />
                  ))}
                </StyledTabs>
                <IconButton
                  onClick={toggleOutputPanel}
                  aria-label="Collapse output panel"
                >
                  <ExpandMoreIcon />
                </IconButton>
              </Div>
              <Div sx={{ flexGrow: 1, overflow: "auto" }}>
                {outputTabs.map((tab, index) => (
                  <TabPanel
                    key={index}
                    value={ioTabValue}
                    index={index}
                    sx={{ p: 0 }}
                  >
                    {outputContent[index]}
                  </TabPanel>
                ))}
              </Div>
            </Card>
          ) : (
            <Card sx={{ display: "flex" }}>
              <StyledTabs
                value={ioTabValue}
                onChange={(_, newValue) => handleIOTabChange(_, newValue)}
                sx={{ flexGrow: 1 }}
                variant="scrollable"
                scrollButtons="auto"
                aria-label="Output panel tabs (collapsed)"
              >
                {outputTabs.map((tab, index) => (
                  <StyledTab
                    key={index}
                    icon={tab.icon}
                    iconPosition="start"
                    label={tab.label}
                    aria-label={tab.label}
                  />
                ))}
              </StyledTabs>
              <IconButton
                onClick={toggleOutputPanel}
                aria-label="Expand output panel"
              >
                <ExpandLessIcon />
              </IconButton>
            </Card>
          )}
        </Panel>
      </PanelGroup>
    </Panel>
  );
};

export default CodeEditorAndOutputPanel;
